import { toIntOrNull } from '@/utils/numberUtilities'
import GridTools from '../legacy/Utils/GridTools'
import forOwn from 'lodash/forOwn'
import omit from 'lodash/omit'
import { reportHeaderType } from '@/enums/reportHeaderType'
import { reportDataType } from '@/enums/reportDataType'
import { definitionType } from '@/enums/definitionType'
import { formatDate } from '@/utils/dateUtilities'
import uuid from 'uuid'

const reportDataTypeEnum = reportDataType.enum
export const tableContentWidthCorrection = -17
export const columnMinAutoWidth = 80
export const rowHeaderMinAutoWidth = 150
export const tableMinHeight = 300
export const tableDefaultHeight = 500
export const handleWidth = 3
export const minColumnWidth = 5

const nonMetadataProps = [
  'isCurrent',
  'isCurrentCol',
  'isHighlighted',
  'editorActive',
  'editorMode',
  'columnSizes',
  'onCollapseToggled',
  'onChange']

const colHeaderUselessProps = [
  'aggregationAllowed',
  'canHaveDynamicRows',
  'depthInTree',
  'hasProgramaticallyExpandedDynamicRows',
  'hasUserExpandedDynamicRows',
  'isDynamicRow',
  'resolvedChildren',
  'treeCode',
  'treeManager',
  'yCoordinate'
]

const rowHeaderUselessProps = [
  'aggregationAllowed',
  'canHaveDynamicRows',
  'depthInTree',
  'hasProgramaticallyExpandedDynamicRows',
  'hasUserExpandedDynamicRows',
  'isDynamicRow',
  'resolvedChildren',
  'treeCode',
  'treeManager'
]

export function getTreeCode (parentTreeCode, code) {
  return parentTreeCode && parentTreeCode !== -1
    ? `${parentTreeCode}.${code}`
    : code
}

export function getPureMetadata (props) {
  return omit(props, nonMetadataProps)
}

export function getColumnInitialSizes (columnCount, tableWidth, loadedSizes) {
  if (loadedSizes && columnCount === loadedSizes.length && !loadedSizes.find(x => x < minColumnWidth)) return loadedSizes
  tableWidth = tableWidth - ((columnCount + 1) * 2)
  const doubleSizeRowHeaders = columnCount > 2
  const rowHeaderSize = doubleSizeRowHeaders ? 2 : 1
  const virtualColumnCount = columnCount + rowHeaderSize - 1
  const singleSize = Math.max(Math.floor(tableWidth / virtualColumnCount), columnMinAutoWidth)

  return [...Array(columnCount)].map((x, i) => {
    if (i === 0) {
      return Math.max(tableWidth - ((virtualColumnCount - rowHeaderSize) * singleSize), rowHeaderMinAutoWidth)
    }
    return singleSize
  })
}

export function isVisibleCell (col, row, mergeCells) {
  return !mergeCells.find(x =>
    col >= x.col && col < x.col + x.colSpan &&
    row >= x.row && row < x.row + x.rowSpan &&
    (row !== x.row || col !== x.col))
}

export function getRowHeaderMetadata (allMetadata, metadata, index, depth, localizeValue) {
  const { name, latinName, treeCode, colSpan, rowSpan, children } = metadata
  const selectedMetadata = omit(metadata, [...nonMetadataProps, ...rowHeaderUselessProps])
  return {
    ...selectedMetadata,
    col: null,
    row: treeCode,
    xCoord: 0,
    yCoord: index,
    value: localizeValue(name, latinName),
    childCount: children ? children.length : 0,
    colSpan: colSpan || 1,
    rowSpan: rowSpan || 1,
    depth
  }
}

export function getColHeaderMetadata (metadata, globalMetadata, localizeValue) {
  const { name, latinName, treeCode, xCoord, yCoord, colSpan, rowSpan, children } = metadata
  const selectedMetadata = omit(metadata, [...nonMetadataProps, ...colHeaderUselessProps])
  return {
    ...selectedMetadata,
    ...globalMetadata,
    col: treeCode,
    row: null,
    xCoord,
    yCoord,
    value: localizeValue(name, latinName),
    childCount: children ? children.length : 0,
    colSpan: colSpan || 1,
    rowSpan: rowSpan || 1
  }
}

export function getBodyCellMetadata (colMetadata, rowMetadata, cellMetadata, tableMetadata, colIndex) {
  const { id: rowId, row: rowTreeCode, yCoord } = rowMetadata
  const { id: colId, col: colTreeCode } = colMetadata
  const singleCellMetadata = cellMetadata ? cellMetadata.find(x => x.colTreeCode === colTreeCode && x.rowTreeCode === rowTreeCode) : null
  const rowDominant = isRowDominant(colMetadata, rowMetadata)
  const reportDataType = GetDataType(colMetadata, rowMetadata, rowDominant)
  if (rowDominant) {
    // switch priority between col and row
    const temp = rowMetadata
    rowMetadata = colMetadata
    colMetadata = temp
  }
  const metadata = getPropsByPriority(['readOnly', 'formula'], singleCellMetadata, colMetadata, rowMetadata)
  return {
    ...tableMetadata,
    ...singleCellMetadata,
    ...metadata,
    reportDataType,
    col: colTreeCode,
    row: rowTreeCode,
    rowId,
    colId,
    xCoord: colIndex,
    yCoord: yCoord,
    value: (singleCellMetadata && singleCellMetadata.data) || null,
    colSpan: (singleCellMetadata && singleCellMetadata.colSpan) || 1,
    rowSpan: 1
  }
}

function GetDataType (col, row, rowDominant) {
  if (rowDominant && row.reportDataType !== reportDataTypeEnum.NoType) {
    return row.reportDataType
  }
  return col.reportDataType === reportDataTypeEnum.NoType ? row.reportDataType : col.reportDataType
}

function isRowDominant (col, row) {
  return row && row.dominant && (!col || !col.dominant)
}

export function getRowTitleMetadata (name, rowSpan) {
  return {
    value: name,
    xCoord: 0,
    yCoord: 0,
    readOnly: true,
    className: 'ht_title',
    colSpan: 1,
    rowSpan: rowSpan || 1
  }
}

export function getTableRowMetadata (row, colsMetadata, rowMetadata, cellMetadata, metadataOverride) {
  const isLineHeader = rowMetadata.reportHeaderType === reportHeaderType.enum.LineHeader
  const isSandwichHeader = rowMetadata.reportHeaderType === reportHeaderType.enum.SandwichHeader
  let colSpanCell = null
  return colsMetadata.map((col, i) => {
    if (i === 0 || isLineHeader || isSandwichHeader) {
      if (isLineHeader || isSandwichHeader) {
        rowMetadata.colSpan = colsMetadata.length
      }
      return { ...metadataOverride, ...rowMetadata }
    }
    if (colSpanCell && i <= (colSpanCell.xCoord + colSpanCell.colSpan - 1)) {
      return colSpanCell
    } else {
      colSpanCell = null
    }
    const bodyCellMetadata = getBodyCellMetadata(col, rowMetadata, cellMetadata, metadataOverride, i)
    if (bodyCellMetadata.colSpan > 1) {
      colSpanCell = bodyCellMetadata
    }
    return bodyCellMetadata
  })
}

function getPropsByPriority (propsName, primary, secondary, ternary, quaternary) {
  const props = {}
  propsName.forEach(prop => {
    const value = getPropByPriority(prop, primary, secondary, ternary, quaternary)
    if (typeof value !== 'undefined') {
      props[prop] = value
    }
  })
  return props
}

function getPropByPriority (propName, primary, secondary, ternary, quaternary) {
  if (primary && primary.hasOwnProperty(propName)) return primary[propName]
  if (secondary && secondary.hasOwnProperty(propName)) return secondary[propName]
  if (ternary && ternary.hasOwnProperty(propName)) return ternary[propName]
  if (quaternary && quaternary.hasOwnProperty(propName)) return quaternary[propName]
}

export function getCoordinatesFromDomElement (el) {
  const tableCell = findAncestorOrSelf(el, 'table-cell')
  if (tableCell) {
    const x = getIntAttr(tableCell, 'data-x')
    const y = getIntAttr(tableCell, 'data-y')
    return { x, y }
  }
  return null
}

function getIntAttr (el, attrName) {
  const attr = el.attributes[attrName]
  return attr ? toIntOrNull(attr.value) : null
}

function findAncestorOrSelf (el, className) {
  while (el && (!el.classList || !el.classList.contains(className))) {
    el = el.parentElement
  }
  return el
}

export function getRowSize (rowSizes, hiddenRows, index, hideFirstRows = 0, defaultHeight = 25) {
  if (hiddenRows[index] || index < hideFirstRows) {
    return 0
  }
  return (rowSizes && rowSizes[`${index}`]) || defaultHeight
}

export function getSelectionRectangle (selectionCol, currentCol, selectionRow, currentRow) {
  if (typeof selectionCol !== 'number') selectionCol = currentCol
  if (typeof selectionRow !== 'number') selectionRow = currentRow
  const left = selectionCol >= currentCol ? currentCol : selectionCol
  const top = selectionRow >= currentRow ? currentRow : selectionRow
  let right = selectionCol < currentCol ? currentCol : selectionCol
  let bottom = selectionRow < currentRow ? currentRow : selectionRow
  return { left, top, right, bottom }
}

export function getSelectionProps (metadata, currentCol, currentRow, selectionCol, selectionRow, columnSizes, rowSizes, hiddenRows) {
  const selection = _getSelectionProps(metadata, currentCol, currentRow, selectionCol, selectionRow, columnSizes, rowSizes, hiddenRows)
  const currentCell = _getSelectionProps(metadata, currentCol, currentRow, currentCol, currentRow, columnSizes, rowSizes, hiddenRows)
  if (!selection || !currentCell) return null
  const {
    left,
    top,
    width,
    height,
    inTopHeader
  } = selection
  return {
    left,
    top,
    width,
    height,
    inTopHeader,
    currentLeft: currentCell.left,
    currentTop: currentCell.top,
    currentWidth: currentCell.width,
    currentHeight: currentCell.height
  }
}

function _getSelectionProps (metadata, currentCol, currentRow, selectionCol, selectionRow, columnSizes, rowSizes, hiddenRows) {
  if (typeof currentCol !== 'number' || typeof currentRow !== 'number') return null
  if (typeof selectionCol !== 'number') selectionCol = currentCol
  if (typeof selectionRow !== 'number') selectionRow = currentRow
  let { left: leftCoord, top: topCoord, right: rightCoord, bottom: bottomCoord } = getSelectionRectangle(selectionCol, currentCol, selectionRow, currentRow)
  const inTopHeader = metadata && metadata[0] && metadata[0][0] && topCoord < metadata[0][0].rowSpan
  const maxColSpan = (metadata || []).filter((x, i) => i >= topCoord && i <= bottomCoord)
    .map((x, i) => x[rightCoord])
    .reduce((a, x) => Math.max(a, x.colSpan), 1)
  if (maxColSpan > 1) {
    rightCoord += maxColSpan - 1
  }

  const left = columnSizes.filter((x, i) => i < leftCoord).reduce((a, i) => a + i, 0)
  const width = columnSizes.filter((x, i) => i >= leftCoord && i <= rightCoord).reduce((a, i) => a + i, 0)
  let top = 0
  for (let i = 0; i < topCoord; i++) {
    top += getRowSize(rowSizes, hiddenRows, i)
  }
  let height = 0
  for (let i = topCoord; i <= bottomCoord; i++) {
    height += getRowSize(rowSizes, hiddenRows, i)
    // for last row selected, check if there is rowspan in any cell
    if (i === bottomCoord && metadata) {
      const maxRowSpan = metadata[i].filter((x, i) => i >= leftCoord && i <= rightCoord).reduce((a, x) => Math.max(a, x.rowSpan), 1)
      if (maxRowSpan > 1) {
        for (let i2 = i; i2 < i + maxRowSpan - 1; i2++) {
          height += getRowSize(rowSizes, hiddenRows, i2)
        }
      }
    }
  }
  return {
    left,
    top,
    width,
    height,
    inTopHeader
  }
}

export function getScrollableItemCount (metadata) {
  return metadata.filter(x => x && x[0] && !x[0].hidden).length
}

export function getMetadataByCoords (metadata, x, y) {
  if (metadata) {
    const rowCells = metadata[y]
    if (rowCells) {
      const targetMetadata = rowCells[x]
      if (targetMetadata) {
        return targetMetadata
      }
    }
  }
  return null
}

export function getTopHeaderRows (sourceHeaders, rowTitle, readonly, globalMetadata, localizeValue) {
  sourceHeaders = processHeaders(sourceHeaders, true, readonly)
  const treeManager = GridTools.processHeaders(sourceHeaders)
  return colHeadersToMatrix(treeManager, rowTitle, globalMetadata, localizeValue)
}

function colHeadersToMatrix (treeManager, rowTitle, globalMetadata, localizeValue) {
  const matrix = []
  let yCoord = 0

  for (let offset = treeManager.maxDepth - 1; offset > -1; offset--) {
    const level = treeManager.getLevelFromCoord(offset)
    const row = []

    if (rowTitle) row.push(getRowTitleMetadata(localizeValue(rowTitle.name, rowTitle.latinName), treeManager.maxDepth)) // Should push row headers title here

    for (let i = 0; i < level.length; i++) {
      if (level[i].yCoord === null) level[i].yCoord = yCoord
      if (level[i].xCoord === null) level[i].xCoord = row.length

      for (let j = 0; j < level[i].colSpan; j++) {
        row.push(getColHeaderMetadata(level[i], globalMetadata, localizeValue))
      }
    }

    yCoord++
    matrix.push(row)
  }
  return matrix
}

export function getRowHeaders (sourceRowHeaders, offset, readOnly, localizeValue) {
  sourceRowHeaders = processHeaders(sourceRowHeaders, false, readOnly)
  const rowHeaders = []
  const getCoordY = () => rowHeaders.length + offset

  function getOrder (key) {
    const item = sourceRowHeaders[key]
    return (item && item.order) || 0
  }

  const readItems = (items, depth, parent) => {
    if (items && items.length > 0) {
      items.sort((a, b) => getOrder(a) - getOrder(b)).forEach(id => {
        const item = sourceRowHeaders[id]
        let metadata = getRowHeaderMetadata(sourceRowHeaders, { ...item, parent }, getCoordY(), depth, localizeValue)
        let headerAbove = null
        let headerBelow = null
        switch (metadata.reportHeaderType) {
          case reportHeaderType.enum.BottomHeader:
            headerBelow = metadata
            break
          case reportHeaderType.enum.SandwichHeader:
            headerAbove = { ...metadata, value: localizeValue(metadata.sandwichName, metadata.latinSandwichName) }
            headerBelow = { ...metadata, reportHeaderType: reportHeaderType.enum.BottomHeader, isBottomOfSandwichHeader: true } // changing to bottom header to identify this is bottom part of sandwich header
            break
          case reportHeaderType.enum.LineHeader:
          default:
            headerAbove = metadata
            break
        }

        if (headerAbove) {
          rowHeaders.push(headerAbove)
        }
        readItems(item.children, depth + 1, item)
        if (headerBelow) {
          headerBelow.yCoord = getCoordY()
          rowHeaders.push(headerBelow)
        }
      })
    }
  }
  const root = sourceRowHeaders && sourceRowHeaders['-1']
  const topItems = root && root.children
  readItems(topItems, 1, root)
  return rowHeaders
}

export function getTreeCodeToIds (dict) {
  const map = {}
  forOwn(dict, (v, k) => {
    map[v.treeCode] = v.id
  })
  return map
}

export function preventAndStop (e) {
  e.preventDefault()
  e.stopPropagation()
}

export function getCellStyles (metadata, columnSizes, rtl) {
  const col = metadata.xCoord || 0
  const colSpan = metadata.colSpan || 1
  const rowSpan = metadata.rowSpan || 1
  const width = columnSizes.filter((x, i) => i >= col && i < col + colSpan).reduce((a, x) => a + x, 0)
  const left = columnSizes.filter((x, i) => i < col).reduce((x, a) => x + a, 0)
  const height = 25 * Math.max(rowSpan, 1)
  const style = {
    width: `${width}px`,
    top: 0,
    height: `${height}px`,
    lineHeight: `${height}px`
  }

  if (rtl) {
    style.right = `${left}px`
  } else {
    style.left = `${left}px`
  }
  // L-header rendering enhancement
  if (metadata.childCount > 0 && metadata.hasData) {
    style.borderBottom = 'none'
  }
  return style
}

export function leftOrRight (rtl, px) {
  return rtl ? { right: px } : { left: px }
}

export function selectionToClipboard ({ metadata, dataItems, selectionCol, currentCol, selectionRow, currentRow }, clipboardData) {
  const selection = getSelectionRectangle(selectionCol, currentCol, selectionRow, currentRow)
  let content = metadata.filter((x, i) => i >= selection.top && i <= selection.bottom)
    .map(row => row.filter((x, i) => i >= selection.left && i <= selection.right).map(x => getValueForClipboard(dataItems, x)).join('\t')).join('\n')
  clipboardData.setData('text/plain', content || '')
}

function getValueForClipboard (dataItems, metadata) {
  if (metadata.col && metadata.row) { // body
    const dataItem = dataItems && dataItems.find(x => x.columnTreeCode === metadata.col && x.rowTreeCode === metadata.row)
    let value = dataItem && dataItem.data
    switch (metadata.reportDataType) {
      case reportDataTypeEnum.Date:
        return formatDate(value)
      case reportDataTypeEnum.Bool:
        return value ? value.toUpperCase() : value
    }
    return value || ''
  }
  return metadata.value || ''
}

export function parseClipboardData (text) {
  return text.split('\n').map(x => x.split('\t').map(x => x.trim()))
}

export function getTableHeight (ref) {
  return Math.max(window.innerHeight - ref.current.getBoundingClientRect().top - 20, tableDefaultHeight)
}

function processHeaders (headers, processHasData, readonly) {
  const processedHeaders = {}

  Object.keys(headers).forEach(key => {
    const header = headers[key]
    const newHeader = { ...header }
    const newChildren = []

    for (let i = 0; i < header.children.length; i++) {
      const child = headers[header.children[i]]

      if (child.definitionType === undefined || !readonly || (readonly && child.definitionType === definitionType.enum.Normal)) {
        newChildren.push(header.children[i])
      }
    }

    if (processHasData &&
      header.hasData &&
      header.children.length &&
      !header.children.find(x => headers[x].treeCode === header.treeCode)) {
      let id = uuid()
      while (headers.hasOwnProperty(id)) {
        id = uuid()
      }

      processedHeaders[id] = { ...header,
        parentId: header.id,
        parent: header.id,
        sameDepthAsParent: true,
        order: header.children.length,
        children: [],
        id
      }

      newChildren.push(id)
    }
    newHeader.children = newChildren
    processedHeaders[newHeader.id.toString()] = newHeader
  })

  return processedHeaders
}



// WEBPACK FOOTER //
// ./src/components/report-tables/core/utils.js